import { useState } from 'react';
import Reveal from '@/components/base/Reveal';
import SectionHeading from '@/components/base/SectionHeading';
import ReviewForm from '@/pages/home/components/ReviewForm';
import { reviews } from '@/mocks/reviews';

function initials(name: string) {
  return name
    .split(' ')
    .map((part) => part.charAt(0))
    .slice(0, 2)
    .join('');
}

const REVIEWS_PER_PAGE = 10;

export default function Reviews() {
  const [visibleCount, setVisibleCount] = useState(REVIEWS_PER_PAGE);

  const total = reviews.length;
  const average = (reviews.reduce((sum, r) => sum + r.rating, 0) / total).toFixed(1);
  const fullStars = Math.floor(Number(average));
  const hasHalf = Number(average) - fullStars >= 0.5;
  const distribution = [5, 4, 3, 2, 1].map((stars) => {
    const count = reviews.filter((r) => r.rating === stars).length;
    return { stars, count, percent: Math.round((count / total) * 100) };
  });

  const visibleReviews = reviews.slice(0, visibleCount);
  const hasMore = visibleCount < total;

  const showMore = () => {
    setVisibleCount((prev) => Math.min(prev + REVIEWS_PER_PAGE, total));
  };

  return (
    <section id="reviews" className="relative w-full px-5 md:px-10 py-20 md:py-28 bg-background-100/40 border-y border-background-200/70">
      <div className="max-w-6xl mx-auto">
        <SectionHeading
          label="Отзывы"
          title="Результаты клиентов, а не обещания"
          description="Мы просим клиентов говорить цифрами: сколько подписчиков пришло, насколько выросли охваты и как это отразилось на заявках."
        />

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
          <Reveal className="lg:col-span-1 h-full">
            <div className="h-full flex flex-col items-center justify-center text-center p-8 rounded-lg bg-background-100/70 border border-background-200/70">
              <span className="font-heading text-6xl font-bold text-foreground-950">{average}</span>
              <div className="mt-3 flex items-center gap-1 text-accent-400">
                {Array.from({ length: fullStars }).map((_, i) => (
                  <i key={i} className="ri-star-fill text-lg"></i>
                ))}
                {hasHalf && <i className="ri-star-half-fill text-lg"></i>}
              </div>
              <p className="mt-3 text-sm text-foreground-600">на основе {total} отзывов</p>
            </div>
          </Reveal>

          <Reveal delay={100} className="lg:col-span-2 h-full">
            <div className="h-full flex flex-col justify-center gap-3 p-8 rounded-lg bg-background-100/70 border border-background-200/70">
              {distribution.map((d) => (
                <div key={d.stars} className="flex items-center gap-3">
                  <span className="w-9 flex-none flex items-center gap-1 text-sm text-foreground-700">
                    {d.stars}
                    <i className="ri-star-fill text-accent-400 text-xs"></i>
                  </span>
                  <div className="flex-1 h-2 rounded-full bg-background-200/70 overflow-hidden">
                    <div className="h-full rounded-full bg-accent-500" style={{ width: `${d.percent}%` }}></div>
                  </div>
                  <span className="w-8 flex-none text-right text-sm text-foreground-600">{d.count}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {visibleReviews.map((review, index) => (
            <Reveal key={review.name} delay={(index % 9) * 60} className="h-full">
              <figure className="h-full flex flex-col p-6 rounded-lg bg-background-100/70 border border-background-200/70 hover:border-primary-400/60 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1 text-accent-400">
                    {Array.from({ length: review.rating }).map((_, starIndex) => (
                      <i key={starIndex} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <i className="ri-double-quotes-r text-2xl text-primary-400/50"></i>
                </div>
                <blockquote className="mt-5 flex-1 text-sm text-foreground-800 leading-relaxed">
                  {review.text}
                </blockquote>
                <figcaption className="mt-6 pt-5 border-t border-background-200/70 flex items-center gap-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500/15 text-primary-300 font-heading text-sm">
                    {initials(review.name)}
                  </span>
                  <span>
                    <span className="block text-sm text-foreground-950">{review.name}</span>
                    <span className="block text-xs text-foreground-600">{review.company}</span>
                  </span>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>

        <div className="mt-10 flex flex-col items-center gap-3">
          <p className="text-sm text-foreground-600">
            Показано {visibleCount} из {total} отзывов
          </p>
          {hasMore && (
            <button
              type="button"
              onClick={showMore}
              className="whitespace-nowrap cursor-pointer inline-flex items-center gap-2 px-8 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors"
            >
              Показать ещё
              <i className="ri-arrow-down-line text-base"></i>
            </button>
          )}
        </div>

        <ReviewForm />
      </div>
    </section>
  );
}